"use client";

import React, { useRef } from "react";
import gsap from "gsap";
import { useGSAP } from "@gsap/react";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { Search, FileText, MapPin, Star, Code as Code2, ChartBar as BarChart3, ArrowRight, CircleCheck as CheckCircle2 } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
gsap.registerPlugin(useGSAP, ScrollTrigger);
const steps = [{
  number: "01",
  title: "Comprehensive SEO Audit",
  icon: Search,
  description: "I uncover structural bottlenecks, crawl issues, and indexation gaps holding your site back.",
  deliverables: ["Technical health diagnostic", "Core Web Vitals & speed assessment", "Competitor opportunity mapping"]
}, {
  number: "02",
  title: "Technical & On-Page Optimization",
  icon: FileText,
  description: "I align your site architecture, meta tags, and copy with real search intent and user expectations.",
  deliverables: ["Targeted keyword map & intent alignment", "URL structure & internal link optimization", "High-converting landing page copy"]
}, {
  number: "03",
  title: "Citations & Local Authority",
  icon: MapPin,
  description: "I build consistent NAP profiles across vetted UK and global directories to boost geographical trust.",
  deliverables: ["Google Business Profile alignment", "NAP consistency verification", "High-trust regional directory listings"]
}, {
  number: "04",
  title: "Reviews & Reputation Strategy",
  icon: Star,
  description: "I establish honest review generation workflows that signal trust to both engines and buyers.",
  deliverables: ["Customer feedback loop integration", "Reputation signal optimization", "Trust badge & social proof placement"]
}, {
  number: "05",
  title: "Schema & Structured Data",
  icon: Code2,
  description: "I write clean JSON-LD schema so search engines and AI engines understand your precise brand entity.",
  deliverables: ["Custom Organization & Service schema", "Rich snippet validation", "AI Search & LLM entity alignment"]
}, {
  number: "06",
  title: "Transparent Reporting & ROI",
  icon: BarChart3,
  description: "I measure organic growth against real commercial outcomes, with zero fluff or vanity metrics.",
  deliverables: ["Custom analytics & conversion dashboard", "Monthly performance breakdown video", "Direct action items for continuous growth"]
}];
export default function Methodology() {
  const containerRef = useRef<HTMLDivElement>(null);
  useGSAP(() => {
    const el = containerRef.current;
    if (!el) return;
    const mm = gsap.matchMedia();
    mm.add("(prefers-reduced-motion: no-preference)", () => {
      gsap.set(".process-card", {
        y: 30
      });
      gsap.to(".process-card", {
        opacity: 1,
        y: 0,
        duration: 0.6,
        stagger: 0.12,
        ease: "power2.out",
        scrollTrigger: {
          trigger: el,
          start: "top 80%",
          once: true
        }
      });
      gsap.set(".connector-line", {
        scaleX: 0
      });
      gsap.to(".connector-line", {
        scaleX: 1,
        duration: 0.8,
        stagger: 0.15,
        ease: "power2.out",
        scrollTrigger: {
          trigger: el,
          start: "top 75%",
          once: true
        }
      });
    });
    mm.add("(prefers-reduced-motion: reduce)", () => {
      gsap.set(".process-card", {
        opacity: 1,
        y: 0
      });
      gsap.set(".connector-line", {
        scaleX: 1
      });
    });
    const refresh = () => ScrollTrigger.refresh();
    requestAnimationFrame(refresh);
    if (document.readyState === "complete") refresh();else window.addEventListener("load", refresh, {
      once: true
    });
    document.fonts?.ready.then(refresh);
  }, {
    scope: containerRef
  });
  return <section id="methodologyprocessflow" data-nav="light" className="bg-background text-foreground py-20 lg:py-28 relative overflow-hidden">
      <div ref={containerRef} className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        <div className="text-center max-w-3xl mx-auto mb-16 lg:mb-20">
          <Badge variant="secondary" className="mb-4 px-3 py-1 text-xs font-semibold uppercase tracking-wider bg-secondary text-secondary-foreground border-border">
            Proven Process
          </Badge>
          <h2 className="font-default text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-foreground">
            My Hands-On SEO Methodology
          </h2>
          <p className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed">
            When you hire me, you get a direct partner who executes a structured, step-by-step strategy tailored specifically to your market and growth goals.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 relative">
          {steps.map((step, i) => {
          const IconComponent = step.icon;
          return <div data-index={i} key={step.number} className="relative flex">
                <Card className="process-card bg-card border-border shadow-sm hover:shadow-md transition-shadow duration-300 w-full flex flex-col justify-between" style={{
              opacity: 0
            }}>
                  <CardHeader className="p-6 pb-4">
                    <div className="flex items-center justify-between mb-4">
                      <span className="inline-flex items-center justify-center size-10 rounded-full bg-primary text-primary-foreground font-bold text-sm shadow-sm">
                        {step.number}
                      </span>
                      <div className="p-2.5 rounded-lg bg-secondary text-primary">
                        <IconComponent className="size-6" />
                      </div>
                    </div>

                    <CardTitle className="font-default text-xl font-bold text-card-foreground">
                      {step.title}
                    </CardTitle>
                    
                    <CardDescription className="text-muted-foreground text-sm leading-relaxed mt-2">
                      {step.description}
                    </CardDescription>
                  </CardHeader>

                  <CardContent className="p-6 pt-2">
                    <div className="connector-line h-0.5 bg-accent/40 w-full mb-4 origin-left" />

                    <div className="space-y-2">
                      <p className="text-xs font-semibold text-foreground tracking-wide uppercase">
                        Key Deliverables:
                      </p>
                      <ul className="space-y-1.5">
                        {step.deliverables.map((item, index) => <li key={index} className="flex items-start gap-2 text-xs text-muted-foreground">
                            <CheckCircle2 className="size-3.5 text-primary shrink-0 mt-0.5" />
                            <span>{item}</span>
                          </li>)}
                      </ul>
                    </div>
                  </CardContent>
                </Card>
              </div>;
        })}
        </div>

        <div className="mt-16 text-center bg-muted/50 border border-border rounded-2xl p-8 max-w-4xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-6">
          <div className="text-left">
            <h3 className="font-default text-xl font-bold text-foreground">
              Ready to execute a tailored strategy?
            </h3>
            <p className="text-sm text-muted-foreground mt-1">
              Work directly with Yassine Essaadi to grow your search visibility.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
            <Button asChild variant="default" size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90">
              <Link to="/ContactPage" className="inline-flex items-center justify-center gap-2">
                Book a Free Consultation
                <ArrowRight className="size-4" />
              </Link>
            </Button>
            <Button asChild variant="outline" size="lg" className="border-border hover:bg-secondary">
              <Link to="/CaseStudiesPage">
                Explore Case Studies
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}